import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { PURCHASE_ORDER_STATES } from '@lensmira/domain'
import { formatDay } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { formatWireForDisplay } from '../money.js'
import { DataSurface } from './DataSurface.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { sentence } from '../sentence.js'
import { purchaseOrderStatusWord } from '../status-labels.js'

/**
 * What the shop has ordered from suppliers, and receiving it. §6.28, §6.29, §6.10 `INV-006`.
 *
 * `GET /purchase-orders` was added in ae89527 because nothing could find an order id. This is the
 * counter's half: see what is outstanding, move it along Appendix C.5, and book the goods in.
 *
 * **It stops at the goods receipt.** Entering a supplier invoice and running `INV-006`'s three-way
 * match are an accountant's job at a desk rather than a counter's at a delivery, and they have
 * their own endpoints; a single screen doing both would be two jobs sharing a layout. That half is
 * still unsurfaced and `later_todo.md` records it.
 *
 * **The over-receipt tolerance is asked for, because nothing stores it.** `INV-006` allows an
 * over-receipt within tolerance and names no figure, and `packages/db/src/tenant/purchasing.ts`
 * says so in terms: "The tolerance is unspecified." So the field has no default here, which is the
 * rule about thresholds this repository applies everywhere. It should be shop policy rather than a
 * number typed per delivery, and that is `later_todo.md` 18.
 *
 * **Appendix C.5 decides the buttons.** `draft -> sent`, `sent -> acknowledged` or `cancelled`,
 * and goods arrive only once acknowledged. A cancelled draft is refused by the server, which is
 * why no cancel is offered on one: an order nobody sent has nothing to cancel with the supplier.
 */

export interface PurchaseOrderLineData {
  readonly id: string
  readonly sku: string
  readonly description: string
  readonly orderedQuantity: number
  readonly receivedQuantity: number
  readonly backorderQuantity: number
  /** Absent means Appendix B's cost row is withheld from this reader. */
  readonly unitCostMinor?: string
  readonly currency?: string
}

export interface PurchaseOrderData {
  readonly id: string
  readonly number: string
  readonly supplierId: string
  /** Appendix C.5's state. */
  readonly status: string
  /** `SUP-005`: the server's own comparison against its clock. */
  readonly overdue: boolean
  readonly backorderQuantity: number
  readonly version: number
  readonly expectedDeliveryOn?: string
  readonly totalMinor?: string
  readonly currency?: string
  readonly lines: readonly PurchaseOrderLineData[]
  readonly receipts: readonly { readonly id: string; readonly number: string }[]
  readonly invoices: readonly {
    readonly id: string
    readonly supplierInvoiceNumber: string
    readonly matchStatus: string
    readonly queryReason?: string
  }[]
}

/**
 * Somewhere at this branch that stock can be put. `BR-10`, `BR-04`.
 *
 * **A goods receipt needs one and nothing produced one.** `ReceiveGoods.stockLocationId` is
 * required as soon as anything accepted becomes stock, the column does not exist on the receipt
 * tables, and until `GET /stock/locations` was added there was no read that returned an id. So the
 * shelf is chosen here, by name, rather than assumed.
 */
export interface StockLocationOption {
  readonly id: string
  readonly name: string
  readonly code: string
  readonly isSelling: boolean
  readonly isCentralWarehouse: boolean
  /** `REF-010`: a trial case. Goods arrive into one, nothing is ever sold out of one. */
  readonly isTrial: boolean
}

export interface PurchasingProps {
  /**
   * The branch's timezone, for `LOC-004`.
   *
   * Appendix J: an instant renders in the branch's zone, never the browser's. A plain `date`
   * column renders in none, which is what `formatDay` is for.
   */
  readonly timezone: string
  readonly load: (status?: string) => Promise<readonly PurchaseOrderData[]>
  /** Where received goods can go. `BR-01`: the order's own branch, which the server checks. */
  readonly loadLocations: () => Promise<readonly StockLocationOption[]>
  /**
   * `stock_receive`, the row the goods receipt takes. Owner, manager and dispenser.
   *
   * **The screen used to offer it to everybody who could open it**, and the rail gated on
   * `purchase_order_raise`, which is owner, manager and **accountant**. So an accountant was shown
   * a Book in button whose write came back 403, and a refused write stops the whole queue until
   * somebody discards it: one delivery booked by the wrong person halted the till.
   */
  readonly canReceive: boolean
  /** `purchase_order_raise`, the row the transitions take. Owner, manager and accountant. */
  readonly canOrder: boolean
}

export function Purchasing({
  load,
  timezone,
  loadLocations,
  canReceive,
  canOrder,
}: PurchasingProps): JSX.Element {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [status, setStatus] = useState('')
  const [rows, setRows] = useState<readonly PurchaseOrderData[] | null>(null)
  const [receiving, setReceiving] = useState<string | null>(null)
  const [quantities, setQuantities] = useState<Record<string, string>>({})
  const [toleranceText, setToleranceText] = useState('')
  const [locations, setLocations] = useState<readonly StockLocationOption[]>([])
  // Apart from `locations.length === 0`, because those are two different situations that the
  // receipt form used to render with one sentence: a branch that genuinely has no shelf, and a
  // shelf read that failed. The second told the shopkeeper to go and add a stock location they
  // already have.
  const [locationsFailed, setLocationsFailed] = useState(false)
  const [locationId, setLocationId] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Apart from `error` because that one carries form validation ("say how many arrived"), and a
  // bad quantity must not turn the order list into a failed load.
  const [loadError, setLoadError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setRows(await load(status === '' ? undefined : status))
      setLoadError(null)
    } catch (failure) {
      setRows(null)
      setLoadError(failure instanceof Error ? failure.message : t('purchasing.loadFailed'))
    }
  }, [load, status, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    let cancelled = false
    void loadLocations()
      .then((loaded) => {
        if (cancelled) return
        setLocations(loaded)
        setLocationsFailed(false)
        // The only shelf there is, is not a choice. More than one is, and nothing here picks for
        // somebody: a delivery booked into the wrong location is stock the counter cannot find.
        if (loaded.length === 1) setLocationId(loaded[0]?.id ?? '')
      })
      // **A failed shelf read is not an unhandled rejection.** Without this the promise rejects
      // into nothing, and the only visible effect is a receipt form whose picker never appears:
      // `locations` stays `[]`, so the form renders `purchasing.noLocations` and tells the
      // shopkeeper this branch has no stock location. It has one. The read failed.
      .catch(() => {
        if (cancelled) return
        setLocations([])
        setLocationsFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [loadLocations])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setReceiving(null)
    // The counted quantities go with the delivery that was booked in. Left on the form, the next
    // delivery against the same order opens with the last one's numbers already typed, and the
    // idempotency key is per request, so sending them again is a second real receipt.
    setQuantities({})
    setToleranceText('')
    void refresh()
  })

  const transition = useCallback(
    async (row: PurchaseOrderData, path: 'send' | 'acknowledge' | 'cancel', label: string) => {
      setError(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/purchase-orders/${row.id}/${path}`,
        label,
        // `ARC-007`: two people moving the same order along is a conflict, not two sends.
        body: { expectedVersion: row.version },
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue],
  )

  const receive = useCallback(
    async (row: PurchaseOrderData) => {
      if (locationId === '') {
        setError(t('purchasing.locationRequired'))
        return
      }
      if (toleranceText.trim() === '') {
        // `INV-006` names no tolerance and nothing stores one, so there is nothing to default to.
        setError(t('purchasing.toleranceRequired'))
        return
      }
      const tolerance = Number(toleranceText.trim())
      if (!Number.isInteger(tolerance) || tolerance < 0) {
        setError(t('purchasing.toleranceInvalid'))
        return
      }
      const lines = row.lines
        .map((line) => ({ line, typed: quantities[line.id] ?? '' }))
        .filter(({ typed }) => typed.trim() !== '')
        .map(({ line, typed }) => ({
          purchaseOrderLineId: line.id,
          quantityReceived: Number(typed.trim()),
        }))
      if (lines.length === 0) {
        setError(t('purchasing.nothingReceived'))
        return
      }
      if (lines.some((l) => !Number.isInteger(l.quantityReceived) || l.quantityReceived < 0)) {
        setError(t('purchasing.quantityInvalid'))
        return
      }
      setError(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/purchase-orders/${row.id}/receipts`,
        label: t('purchasing.queueReceiveLabel', { number: row.number }),
        body: {
          expectedVersion: row.version,
          overReceiptToleranceBasisPoints: tolerance,
          stockLocationId: locationId,
          lines,
        },
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, locationId, quantities, t, toleranceText],
  )

  const line = statusLine(t, snapshot, writeId, t('queue.subject.purchaseOrder'))

  return (
    <section className="lm-screen" aria-labelledby="purchasing-heading">
      <h1 id="purchasing-heading" className="lm-screen__heading">
        {t('purchasing.heading')}
      </h1>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('purchasing.statusLabel')}</span>
        <select
          className="lm-input"
          value={status}
          onChange={(e) => {
            setStatus(e.currentTarget.value)
          }}
        >
          <option value="">{t('purchasing.statusOpen')}</option>
          {/* Appendix C.5's own list, so a state the state machine gains appears here without a
              second table to remember, and the words are the ones every other screen uses. */}
          {PURCHASE_ORDER_STATES.map((state) => (
            <option key={state} value={state}>
              {purchaseOrderStatusWord(t, state)}
            </option>
          ))}
        </select>
      </label>

      {error === null ? null : (
        <p className="lm-hint" role="alert">
          {error}
        </p>
      )}

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      {/*
        §8 `ST-02`. The controls above stay outside the surface so the status filter is still there
        when the list is empty or failed. Empty is decided by the filter and not the row count:
        the unfiltered view is the server's "still open", so an empty one says nothing is open and
        never claims the shop has not ordered. Partial is the cost grant (`ROLE-001a`) withholding
        totals from a reader without it, which is policy and shows on every visit for them.
      */}
      <DataSurface
        state={
          loadError !== null
            ? { kind: 'error', message: loadError, onRetry: () => void refresh() }
            : rows === null
              ? { kind: 'loading' }
              : rows.length === 0
                ? status === ''
                  ? { kind: 'empty' }
                  : { kind: 'filtered' }
                : rows.some((r) => r.totalMinor === undefined)
                  ? { kind: 'partial', missing: t('purchasing.missingCosts') }
                  : { kind: 'ready' }
        }
        shape="rows"
        rows={4}
        what={t('purchasing.what')}
        emptyMessage={t('purchasing.empty')}
        filteredMessage={t('purchasing.filtered', { status: purchaseOrderStatusWord(t, status) })}
      >
        <ul className="lm-po-list">
          {(rows ?? []).map((row) => (
            <li key={row.id} className="lm-po">
              <span className="lm-po__number" dir="ltr">
                <Ltr>{row.number}</Ltr>
              </span>
              <span className="lm-po__status">{purchaseOrderStatusWord(t, row.status)}</span>

              {row.expectedDeliveryOn === undefined ? (
                <span className="lm-po__when">{t('purchasing.noDatePromised')}</span>
              ) : (
                <span className="lm-po__when">
                  {sentence(t, 'purchasing.expected', {
                    date: <Ltr>{formatDay(row.expectedDeliveryOn, timezone, locale)}</Ltr>,
                  })}
                </span>
              )}

              {row.overdue ? (
                // `SUP-005`, and the server's comparison rather than this device's clock: a wrong
                // clock here would have a shop chasing a supplier who is not late.
                <span className="lm-po__overdue">{t('purchasing.overdue')}</span>
              ) : null}

              {row.totalMinor === undefined || row.currency === undefined ? null : (
                <span className="lm-po__total" dir="ltr">
                  {formatWireForDisplay(row.totalMinor, row.currency)}
                </span>
              )}

              {row.backorderQuantity > 0 ? (
                <span className="lm-po__backorder">
                  {t('purchasing.backorder', { count: row.backorderQuantity })}
                </span>
              ) : null}

              {/* Appendix C.5: `draft -> sent` only. */}
              {canOrder && row.status === 'draft' ? (
                <button
                  type="button"
                  className="lm-button lm-button--primary"
                  onClick={() => void transition(row, 'send', t('purchasing.queueSendLabel'))}
                >
                  {t('purchasing.send')}
                </button>
              ) : null}

              {/* `sent -> acknowledged` or `cancelled`. Both, and only here: a draft nobody sent
                  has nothing to cancel with the supplier, which the server refuses. */}
              {canOrder && row.status === 'sent' ? (
                <>
                  <button
                    type="button"
                    className="lm-button lm-button--primary"
                    onClick={() =>
                      void transition(row, 'acknowledge', t('purchasing.queueAckLabel'))
                    }
                  >
                    {t('purchasing.acknowledge')}
                  </button>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => void transition(row, 'cancel', t('purchasing.queueCancelLabel'))}
                  >
                    {t('purchasing.cancel')}
                  </button>
                </>
              ) : null}

              {/* Goods arrive once the supplier has acknowledged, and again while partly received. */}
              {canReceive &&
              (row.status === 'acknowledged' || row.status === 'partially_received') ? (
                <button
                  type="button"
                  className="lm-button lm-button--primary"
                  onClick={() => {
                    setReceiving(receiving === row.id ? null : row.id)
                    setError(null)
                  }}
                  aria-expanded={receiving === row.id}
                >
                  {t('purchasing.bookIn')}
                </button>
              ) : null}

              {receiving === row.id ? (
                <div className="lm-po__receipt">
                  <table className="lm-line-table">
                    <caption className="lm-rx-grid__caption">
                      {t('purchasing.receiptCaption', { number: row.number })}
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">{t('purchasing.col.item')}</th>
                        <th scope="col">{t('purchasing.col.ordered')}</th>
                        <th scope="col">{t('purchasing.col.alreadyIn')}</th>
                        <th scope="col">{t('purchasing.col.arrived')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {row.lines.map((orderLine) => (
                        <tr key={orderLine.id}>
                          <th scope="row" className="lm-line-table__description">
                            {orderLine.description}
                            <span className="lm-line-table__meta" dir="ltr">
                              <Ltr>{orderLine.sku}</Ltr>
                            </span>
                          </th>
                          <td className="lm-line-table__num" dir="ltr">
                            {orderLine.orderedQuantity}
                          </td>
                          <td className="lm-line-table__num" dir="ltr">
                            {orderLine.receivedQuantity}
                          </td>
                          <td className="lm-line-table__num">
                            <label>
                              <span className="lm-visually-hidden">
                                {t('purchasing.arrivedFor', {
                                  description: orderLine.description,
                                })}
                              </span>
                              <input
                                className="lm-input"
                                inputMode="numeric"
                                autoComplete="off"
                                value={quantities[orderLine.id] ?? ''}
                                onChange={(e) => {
                                  const value = e.currentTarget.value
                                  setQuantities((previous) => ({
                                    ...previous,
                                    [orderLine.id]: value,
                                  }))
                                }}
                              />
                            </label>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {locations.length === 0 ? (
                    // A shop with no stock location cannot book goods in at all, and the server
                    // would refuse the receipt. Said here rather than after the attempt. A failed
                    // read says so instead: the shelves may well be there.
                    <p className="lm-hint">
                      {t(locationsFailed ? 'purchasing.locationsFailed' : 'purchasing.noLocations')}
                    </p>
                  ) : (
                    <label className="lm-field lm-field--inline">
                      <span className="lm-field__label">{t('purchasing.locationLabel')}</span>
                      <select
                        className="lm-input"
                        value={locationId}
                        onChange={(e) => {
                          setLocationId(e.currentTarget.value)
                        }}
                      >
                        <option value="">{t('purchasing.locationUnset')}</option>
                        {locations.map((location) => (
                          <option key={location.id} value={location.id}>
                            {/* `BR-10` and `BR-04` travel with the name, because a back store and
                                the central warehouse are both real places to put a delivery and
                                the counter cannot sell from either. */}
                            {location.isCentralWarehouse
                              ? t('purchasing.locationWarehouse', { name: location.name })
                              : location.isSelling
                                ? location.name
                                : t('purchasing.locationNotSelling', { name: location.name })}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}

                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">{t('purchasing.toleranceLabel')}</span>
                    <input
                      className="lm-input"
                      inputMode="numeric"
                      autoComplete="off"
                      value={toleranceText}
                      onChange={(e) => {
                        setToleranceText(e.currentTarget.value)
                      }}
                    />
                  </label>
                  {/* No default, because `INV-006` names none and nothing stores one. Said here
                      rather than left as an unexplained required field. */}
                  <p className="lm-hint">{t('purchasing.toleranceHint')}</p>

                  <button
                    type="button"
                    className="lm-button lm-button--primary"
                    onClick={() => void receive(row)}
                  >
                    {t('purchasing.recordReceipt')}
                  </button>
                </div>
              ) : null}

              {row.receipts.length === 0 ? null : (
                <span className="lm-po__receipts">
                  {t('purchasing.receiptsSoFar', {
                    numbers: row.receipts.map((r) => r.number).join(', '),
                  })}
                </span>
              )}

              {row.invoices.length === 0 ? null : (
                <span className="lm-po__invoices">
                  {/* `INV-006`'s match status, read only here: entering an invoice and resolving a
                      query are an accountant's job and are not on this screen. */}
                  {t('purchasing.invoices', {
                    detail: row.invoices
                      .map((i) => `${i.supplierInvoiceNumber}: ${i.matchStatus}`)
                      .join(', '),
                  })}
                </span>
              )}
            </li>
          ))}
        </ul>
      </DataSurface>
    </section>
  )
}
